import { ChangeEvent } from 'react';
import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  PathValue
} from 'react-hook-form';

export type MapperProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  THtmlElement = HTMLInputElement
> = {
  inputMapper?: InputMapper<TFieldValues, TName>;
  outputMapper?: OutputMapper<TFieldValues, TName, THtmlElement>;
};

/**
 * Input -> RHF
 */
export type OutputMapper<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  THtmlElement = HTMLInputElement
> = (
  event: ChangeEvent<THtmlElement>,
  currentValue: PathValue<TFieldValues, TName>
) => PathValue<TFieldValues, TName>;

/**
 * RHF -> Input
 */
export type InputMapper<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = (currentValue: PathValue<TFieldValues, TName>) => unknown;

export function withMapper<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  THtmlElement = Element
>(
  field: ControllerRenderProps<TFieldValues, TName>,
  inputMapper?: InputMapper<TFieldValues, TName>,
  outputMapper?: OutputMapper<TFieldValues, TName, THtmlElement>
): ControllerRenderProps<TFieldValues, TName> {
  if (inputMapper) {
    field.value = inputMapper(field.value) as PathValue<TFieldValues, TName>;
  }

  if (outputMapper) {
    const previousOnChange = field.onChange;
    field.onChange = (event) => {
      previousOnChange(outputMapper(event, field.value));
    };
  }

  return field;
}
